/**
 * Fund chip with manager logo — port of the OSDK FundChip (logo map + initials
 * fallback with deterministic colors). Logos live in /public/logos (from the
 * original portal assets).
 */
import { cn } from '../utils.js';

const FUND_LOGOS: Record<string, string> = {
  'איביאי': 'ibi.png',
  'אינפיניטי': 'infinity.png',
  'אלטשולר שחם': 'altshuler.png',
  'אנליסט': 'analyst.png',
  'אקסלנס': 'excellence.png',
  'הראל': 'harel.png',
  'ילין לפידות': 'yelin.png',
  'מגדל': 'migdal.png',
  'מור': 'mor.png',
  'מיטב דש': 'meitav.png',
  'פעילים': 'peilim.png',
};

const FALLBACK_COLORS = [
  'bg-blue-600/20 text-blue-300',
  'bg-emerald-600/20 text-emerald-300',
  'bg-amber-600/20 text-amber-300',
  'bg-purple-600/20 text-purple-300',
  'bg-rose-600/20 text-rose-300',
  'bg-cyan-600/20 text-cyan-300',
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/** Logo file for a fund/manager name — base name first, then contains-match (handles 'פעילים ישן'). */
export function logoFor(name: string): string | null {
  if (FUND_LOGOS[name]) return FUND_LOGOS[name];
  for (const [key, file] of Object.entries(FUND_LOGOS)) {
    if (name.includes(key)) return file;
  }
  return null;
}

export function FundLogo({ name, size = 22 }: { name: string; size?: number }) {
  const file = logoFor(name);
  if (file) {
    return (
      <img
        src={`/logos/${file}`}
        alt={name}
        width={size}
        height={size}
        className="rounded-full bg-white/90 object-contain p-0.5"
      />
    );
  }
  const color = FALLBACK_COLORS[hashString(name) % FALLBACK_COLORS.length];
  return (
    <span
      className={cn('inline-flex items-center justify-center rounded-full text-[10px] font-bold', color)}
      style={{ width: size, height: size }}
    >
      {name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('')}
    </span>
  );
}

export function FundChip({
  name,
  active = false,
  onClick,
}: {
  name: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // font weight/size stay CONSTANT so selection never changes chip width (no layout jerk)
        'flex shrink-0 snap-start items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
        active ? 'border-ring bg-muted text-foreground' : 'border-border text-muted-foreground hover:bg-muted/60',
      )}
    >
      <FundLogo name={name} />
      {name}
    </button>
  );
}
